/**
 * Project Sources (child route of the app shell), two tabs kept in the URL (?tab=review):
 * Origins lists, filters and uploads the raw inputs; "Waiting for the wiki" is the review queue.
 */

import { useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { Upload } from 'lucide-react'
import { toast } from 'sonner'
import type { SourceStatus } from '@agentwiki/shared'
import { useDebounce } from '../hooks/use-debounce'
import { usePushSource, useSources, useWikiProposals } from '../hooks/use-project-content'
import { useAppStore } from '../stores/app-store'
import { projectPath } from '../components/shell/nav-config'
import { useT } from '../i18n'
import type { TranslationKey } from '../i18n'
import { formatBytes } from '../lib/format-bytes'
import { cn } from '../lib/utils'
import { ProjectReviewPanel } from './project-review'

type StatusFilter = SourceStatus | 'all'
const STATUSES: Array<{ value: StatusFilter; key: TranslationKey }> = [
  { value: 'active', key: 'sources.status.active' },
  { value: 'superseded', key: 'sources.status.superseded' },
  { value: 'archived', key: 'sources.status.archived' },
  { value: 'all', key: 'sources.status.all' },
]

type SourcesTab = 'origins' | 'review'
const TABS: Array<{ value: SourcesTab; key: TranslationKey }> = [
  { value: 'origins', key: 'sources.tab.origins' },
  { value: 'review', key: 'sources.tab.review' },
]
const tabId = (tab: SourcesTab) => `sources-tab-${tab}`
const panelId = (tab: SourcesTab) => `sources-panel-${tab}`

export function ProjectSourcesPage() {
  const t = useT()
  const { projectSlug = 'default' } = useParams<{ projectSlug: string }>()
  const [params, setParams] = useSearchParams()
  const tab: SourcesTab = params.get('tab') === 'review' ? 'review' : 'origins'
  // `pending` counts every waiting proposal; the review tab shares this query while it shows the default filter
  const { data: proposals } = useWikiProposals(projectSlug, 'pending')
  const pending = proposals?.pending ?? 0

  const selectTab = (next: SourcesTab) => {
    setParams((prev) => {
      const nextParams = new URLSearchParams(prev)
      if (next === 'origins') nextParams.delete('tab')
      else nextParams.set('tab', next)
      return nextParams
    }, { replace: true })
  }

  // WAI-ARIA tabs: arrow keys, Home and End move between tabs and select them
  const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const index = TABS.findIndex((item) => item.value === tab)
    const target = event.key === 'ArrowRight' ? (index + 1) % TABS.length
      : event.key === 'ArrowLeft' ? (index - 1 + TABS.length) % TABS.length
        : event.key === 'Home' ? 0
          : event.key === 'End' ? TABS.length - 1
            : -1
    if (target < 0) return
    event.preventDefault()
    selectTab(TABS[target].value)
    document.getElementById(tabId(TABS[target].value))?.focus()
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-4 py-6 md:px-8 md:py-10">
      <header className="min-w-0 space-y-1">
        <h1 className="font-display text-2xl font-semibold tracking-tight text-fg">{t('sources.title')}</h1>
        <p className="max-w-2xl text-sm text-fg-muted">{t(tab === 'review' ? 'review.subtitle' : 'sources.subtitle')}</p>
      </header>

      <div role="tablist" aria-label={t('sources.tabs')} className="flex gap-1 overflow-x-auto border-b border-border">
        {TABS.map((item) => {
          const selected = tab === item.value
          return (
            <button
              key={item.value}
              id={tabId(item.value)}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={panelId(item.value)}
              tabIndex={selected ? 0 : -1}
              onClick={() => selectTab(item.value)}
              onKeyDown={onTabKeyDown}
              className={cn(
                '-mb-px inline-flex min-h-9 shrink-0 cursor-pointer items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium',
                selected ? 'border-accent text-fg' : 'border-transparent text-fg-muted hover:text-fg',
              )}
            >
              {t(item.key)}
              {item.value === 'review' && pending > 0 && (
                <span className="rounded-full bg-accent-soft px-1.5 py-0.5 text-xs tabular-nums text-accent-text">
                  <span aria-hidden="true">{pending}</span>
                  <span className="sr-only">{t('review.pending', { count: pending })}</span>
                </span>
              )}
            </button>
          )
        })}
      </div>

      <div role="tabpanel" id={panelId(tab)} aria-labelledby={tabId(tab)}>
        {tab === 'review' ? <ProjectReviewPanel projectSlug={projectSlug} /> : <SourceOriginsPanel projectSlug={projectSlug} />}
      </div>
    </div>
  )
}

/** Origins tab: the raw inputs (read-only once pushed), with status filter, search and upload */
function SourceOriginsPanel({ projectSlug }: { projectSlug: string }) {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const [status, setStatus] = useState<StatusFilter>('active')
  const [search, setSearch] = useState('')
  const debounced = useDebounce(search.trim(), 300)
  const { data, isLoading, error } = useSources(projectSlug, { status, search: debounced || undefined })
  const push = usePushSource(projectSlug)
  const fileInput = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const onFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ''
    if (!files.length) return
    setUploading(true)
    let uploaded = 0
    for (const file of files) {
      const content = await file.text()
      if (content.includes('\u0000')) {
        toast.error(t('sources.notText', { name: file.name }))
        continue
      }
      try {
        const result = await push.mutateAsync({
          filename: file.name,
          content,
          contentType: file.type || 'text/plain',
          origin: 'web',
        })
        if (result.deduplicated) toast.info(t('sources.unchanged', { name: file.name }))
        else uploaded++
      } catch (err) {
        toast.error(t('sources.uploadFailed', { name: file.name, message: err instanceof Error ? err.message : String(err) }))
      }
    }
    setUploading(false)
    if (uploaded) toast.success(t('sources.uploaded', { count: uploaded }))
  }

  const dateFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' })

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-border p-0.5" role="group">
          {STATUSES.map((s) => (
            <button
              key={s.value}
              type="button"
              aria-pressed={status === s.value}
              onClick={() => setStatus(s.value)}
              className={`cursor-pointer rounded-md px-3 py-1.5 text-sm ${status === s.value ? 'bg-surface-hover text-fg' : 'text-fg-muted hover:text-fg'}`}
            >
              {t(s.key)}
            </button>
          ))}
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('sources.search')}
          className="min-w-0 flex-1 rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:border-accent sm:max-w-xs"
        />
        <input ref={fileInput} type="file" multiple className="hidden" onChange={(e) => void onFiles(e)} />
        <button
          type="button"
          disabled={uploading}
          onClick={() => fileInput.current?.click()}
          className="ml-auto inline-flex cursor-pointer items-center gap-2 rounded-lg bg-accent px-3.5 py-2 text-sm font-medium text-accent-fg hover:bg-accent-hover disabled:opacity-60"
        >
          <Upload className="h-4 w-4" aria-hidden="true" />
          {uploading ? t('sources.uploading') : t('sources.upload')}
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-fg-subtle">{t('common.loading')}</p>
      ) : error ? (
        <p role="alert" className="text-sm text-danger">{t('sources.loadError')}</p>
      ) : !data?.sources.length ? (
        <div className="space-y-3 rounded-xl border border-dashed border-border p-6 text-sm text-fg-muted">
          <p>{t('sources.empty')}</p>
          <pre className="overflow-x-auto rounded-lg bg-surface-muted p-3 text-xs text-fg">{`agentwiki project use ${projectSlug}\nagentwiki source push docs/ --origin claude-code`}</pre>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead className="whitespace-nowrap bg-surface-muted text-xs text-fg-subtle">
              <tr>
                <th className="px-4 py-2.5 font-medium">{t('sources.col.file')}</th>
                <th className="px-4 py-2.5 font-medium">{t('sources.col.origin')}</th>
                <th className="px-4 py-2.5 text-right font-medium">{t('sources.col.size')}</th>
                <th className="px-4 py-2.5 font-medium">{t('sources.col.added')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.sources.map((s) => (
                <tr key={s.id} className="hover:bg-surface-hover">
                  <td className="w-full max-w-0 px-4 py-2.5">
                    <Link to={projectPath(projectSlug, `sources/${s.id}`)} className="block truncate font-medium text-fg hover:text-accent-text">
                      {s.title}
                    </Link>
                    <span className="block truncate text-xs text-fg-subtle">
                      {s.filename}
                      {s.status !== 'active' && ` · ${t(`sources.status.${s.status}`)}`}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-fg-muted">{s.origin}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums text-fg-muted">{formatBytes(s.sizeBytes, locale)}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-fg-muted">{dateFormat.format(new Date(s.createdAt))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="border-t border-border px-4 py-2 text-xs text-fg-subtle">
            {t('sources.count', { shown: data.sources.length, total: data.total })}
          </p>
        </div>
      )}
    </div>
  )
}
